/**
 * Treatment Goals API service
 */

import authService from "./auth";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface User {
  id: number;
  full_name: string;
  email: string;
}

export interface Domain {
  id: number;
  name: string;
  goals?: Goal[];
}

export interface Goal {
  id: number;
  domain_id: number;
  goal_text: string;
  author: User;
  domain?: Domain;
  is_approved: boolean;
  approved_by?: User | null;
  approved_at?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface DomainCreate {
  name: string;
}

export interface GoalCreate {
  domain_id: number;
  goal_text: string;
}

export interface GoalUpdate {
  domain_id?: number;
  goal_text?: string;
}

// Domain API calls
export async function getDomains(): Promise<Domain[]> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-goals/domains`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to fetch domains");
  }

  return response.json();
}

export async function createDomain(domain: DomainCreate): Promise<Domain> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-goals/domains`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(domain),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to create domain");
  }

  return response.json();
}

export async function updateDomain(
  domainId: number,
  updates: Partial<DomainCreate>
): Promise<Domain> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-goals/domains/${domainId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to update domain");
  }

  return response.json();
}

export async function deleteDomain(domainId: number): Promise<void> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-goals/domains/${domainId}`,
    {
      method: "DELETE",
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to delete domain");
  }
}

// Goal API calls
export async function getGoals(
  domainId?: number,
  authorId?: number,
  showPending?: boolean
): Promise<Goal[]> {
  const params = new URLSearchParams();
  if (domainId) params.append("domain_id", domainId.toString());
  if (authorId) params.append("author_id", authorId.toString());
  if (showPending) params.append("show_pending", "true");

  const url = `${API_BASE_URL}/api/treatment-goals/goals?${params.toString()}`;

  const response = await authService.authenticatedFetch(url);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to fetch goals");
  }

  return response.json();
}

export async function createGoal(goal: GoalCreate): Promise<Goal> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-goals/goals`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(goal),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to create goal");
  }

  return response.json();
}

export async function updateGoal(
  goalId: number,
  updates: GoalUpdate
): Promise<Goal> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-goals/goals/${goalId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to update goal");
  }

  return response.json();
}

export async function deleteGoal(goalId: number): Promise<void> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-goals/goals/${goalId}`,
    {
      method: "DELETE",
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to delete goal");
  }
}

export async function approveGoal(goalId: number): Promise<Goal> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-goals/goals/${goalId}/approve`,
    {
      method: "POST",
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to approve goal");
  }

  return response.json();
}

export async function rejectGoal(goalId: number): Promise<Goal> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-goals/goals/${goalId}/reject`,
    {
      method: "POST",
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to reject goal");
  }

  return response.json();
}

export async function getGoalAuthors(): Promise<User[]> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-goals/goals/authors`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to fetch authors");
  }

  return response.json();
}

export async function getAllUsers(): Promise<User[]> {
  const response = await authService.authenticatedFetch(
    `${API_BASE_URL}/api/treatment-goals/users`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to fetch users");
  }

  return response.json();
}
